import React, { Fragment, forwardRef, useMemo } from 'react'

import { XCircleIcon } from '@heroicons/react/20/solid'
import { ICellEditorParams } from 'ag-grid-community'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import Select, { OptionProps, components } from 'react-select'
import * as yup from 'yup'

import { OptionWithAvatar } from '@/components/ReactSelectCustomControls'
import Spinner from '@/components/Spinner'
import { dealTeams } from '@/constants/dealTeams'
import { useCurrentApplication } from '@/graphql/services/application'
import { useDeleteDealTeamMutation, useUpsertDealTeamMutation } from '@/services/application'
import { useGetLoggedUserQuery, useGetStaffUsersQuery } from '@/services/user'
import { Option } from '@/types/components'
import {
  assignDealTeamDisabled,
  getUserFullNameOrEmail,
  isAllowedToAssignSales,
  staffUserGroupsAvailableInWorkviews,
} from '@/utils/user'

// Custom Option component with tooltip for disabled Sales option
const CustomOption = (props: OptionProps<any>) => {
  const { data, isDisabled, children } = props
  const isSalesOption = data.value === 'sales'

  return (
    <components.Option {...props}>
      <div className='w-full group'>
        <div
          className={`${isSalesOption && isDisabled ? 'tooltip group-hover:tooltip-open tooltip-right' : ''}`}
          data-tip={
            isSalesOption && isDisabled ? 'You do not have permission to assign a Sales user.' : ''
          }
        >
          <div className='text-left relative'>{children}</div>
        </div>
      </div>
    </components.Option>
  )
}

export const DealTeamCellEditor = forwardRef(function DealTeamCellEditor(props: ICellEditorParams) {
  const { data: staffUsers, isLoading: isLoadingStaffUsers } = useGetStaffUsersQuery()
  const [upsertDealTeam] = useUpsertDealTeamMutation()
  const [deleteDealTeam] = useDeleteDealTeamMutation()
  const { data: loggedUser } = useGetLoggedUserQuery()
  const { data: application } = useCurrentApplication()

  const staffUsersOptions = useMemo(
    () =>
      staffUsers
        ?.filter((user) => user?.is_active)
        .filter((user) =>
          user.groups.some((group) => staffUserGroupsAvailableInWorkviews.includes(group.name))
        )
        .map((user) => ({
          label: getUserFullNameOrEmail(user),
          value: user.id,
          user, // required for the avatar
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [staffUsers, isLoadingStaffUsers]
  )

  const getUserDetails = (userId) => staffUsers.find((u) => u.id === userId)

  const handleSubmit = (values, { setErrors }) => {
    const { node } = props
    const { user, type } = values

    upsertDealTeam({
      application: node.data.id,
      user,
      type,
    }).then(({ data, error }: any) => {
      if (error) {
        setErrors({ type: `Someone from the ${type} team is already assigned to this application` })
      } else {
        // Optimistically update the deal team
        node.setData({
          ...node.data,
          deal_teams: [...node.data.deal_teams, { ...data }],
        })
        props.stopEditing()
      }
    })
  }

  const handleUserRemove = (dealTeam) => {
    const { node } = props

    deleteDealTeam(dealTeam.uuid).then(() => {
      // Optimistically update the deal team
      const dealTeams = node.data.deal_teams.filter((dt) => dt.user !== dealTeam.user)
      node.setData({ ...node.data, deal_teams: dealTeams })
      props.stopEditing()
    })
  }

  const isOptionDisabled = (option: Option) => {
    if (option?.value === 'sales' && !isAllowedToAssignSales(loggedUser, application)) {
      return true
    }

    return false
  }

  return (
    <div className='p-4'>
      {isLoadingStaffUsers && <Spinner size={4} />}
      {staffUsers && (
        <>
          <Formik
            initialValues={{ user: '', type: '' }}
            validationSchema={yup.object({
              user: yup.string().required('User is required'),
              type: yup.string().required('Group is required'),
            })}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, isValid }) => (
              <Form>
                <Field
                  name='name'
                  placeholder='Select a user'
                  component={Select}
                  options={staffUsersOptions}
                  styles={{
                    control: (baseStyles) => ({ ...baseStyles, minWidth: '384px' }),
                  }}
                  onChange={(e) => setFieldValue('user', e.value)}
                  components={{ Option: OptionWithAvatar }}
                />
                <ErrorMessage
                  name='user'
                  component='small'
                  className='text-red-500 text-sm'
                />
                <Field
                  name='type'
                  className='mt-2'
                  placeholder='Select a group'
                  component={Select}
                  options={Object.keys(dealTeams).map((key) => ({
                    value: key,
                    label: dealTeams[key].label,
                  }))}
                  isDisabled={assignDealTeamDisabled(loggedUser, application)}
                  isOptionDisabled={isOptionDisabled}
                  onChange={(e) => setFieldValue('type', e.value)}
                  components={{ Option: CustomOption }}
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />

                <ErrorMessage
                  name='type'
                  component='small'
                  className='text-red-500 text-sm'
                />
                <div className='mt-2 flex justify-end'>
                  <button
                    className='btn btn-primary btn-sm w-1/6'
                    type='submit'
                    disabled={!isValid}
                  >
                    Add
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </>
      )}

      <div>
        {props?.data.deal_teams?.length > 0 ? (
          <>
            <div className='py-4'>People assigned to this deal:</div>
            <ul className='border p-4 rounded bg-white shadow-lg grid grid-cols-2 gap-2'>
              {props?.data.deal_teams?.map((dt) => {
                const isSalesAndNotAllowed =
                  dt.type === 'sales' && !isAllowedToAssignSales(loggedUser, application)

                return (
                  <li
                    key={dt.user?.id}
                    className='flex items-center justify-around text-white px-4 py-2 rounded shadow-md font-semibold'
                    style={{ background: dealTeams[dt.type]?.color ?? '#000' }}
                  >
                    {getUserDetails(dt.user)?.first_name && getUserDetails(dt.user)?.last_name ? (
                      <>
                        {getUserDetails(dt.user)?.first_name} {getUserDetails(dt.user)?.last_name}
                      </>
                    ) : (
                      <>{getUserDetails(dt.user)?.username}</>
                    )}
                    <span className='mx-1'>|</span>
                    <span>{dealTeams[dt.type]?.label}</span>
                    <span className='ml-2'>
                      <span
                        className={isSalesAndNotAllowed ? 'tooltip tooltip-top' : ''}
                        data-tip='You do not have permission to remove this user.'
                      >
                        <XCircleIcon
                          width={20}
                          height={20}
                          className={
                            isSalesAndNotAllowed
                              ? 'cursor-not-allowed text-gray-400'
                              : 'cursor-pointer text-white hover:text-gray-200'
                          }
                          onClick={() => {
                            if (!isSalesAndNotAllowed) {
                              handleUserRemove(dt)
                            }
                          }}
                        />
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          </>
        ) : (
          <div className='pt-4'>Nobody is currently assigned to this deal.</div>
        )}
      </div>
    </div>
  )
})
